<template>
  <div class="admin-posters">
    <h1>海報管理</h1>
    <!-- 搜尋 & 分頁(示範) -->
    <div class="search-bar">
      <input
        v-model="keyword"
        placeholder="關鍵字搜尋"
        @keyup.enter="onSearch"
        class="search-input"
      />
      <button class="btn btn-primary" @click="onSearch">搜尋</button>
      <!-- 新增海報按鈕：手機版會換到下一行 -->
      <button class="btn btn-success" @click="openCreateModal">新增海報</button>
    </div>

    <!-- 顯示海報列表 -->
    <div v-if="posters.length" class="table-container">
      <table class="poster-table">
        <thead>
          <tr>
            <th>啟用</th>
            <th>標題</th>
            <th>封面</th>
            <th>ID</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <!-- 使用 data-label 讓手機版顯示欄位名稱 -->
          <tr v-for="item in posters" :key="item.id">
            <td data-label="啟用">
              <label class="checkbox-label">
                <label class="switch">
                <input
                  type="checkbox"
                  v-model="item.is_active"
                  :true-value="1"
                  :false-value="0"
                  @change="updatePosterIsActive(item)"
                />
                <span class="slider"></span>
                </label>
                <span class="check-label">
                  {{ item.is_active == 1 ? '顯示' : '隱藏' }}
                </span>
            </label>
            </td>
            <td data-label="標題">{{ item.title }}</td>
            <td data-label="封面">
              <img
                v-if="item.coverImage"
                :src="item.coverImage"
                alt="cover"
                class="cover-image"
              />
            </td>
           
            <td data-label="ID">{{ item.id }}</td>
            <td data-label="操作">
              <!-- 編輯刪除之間帶點間距，CSS 調整 .btn + .btn -->
              <button class="btn btn-info" @click="openEditModal(item)">編輯</button>
              <button class="btn btn-danger" @click="deletePoster(item.id)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p class="no-data">暫無海報資料</p>
    </div>

    <!-- 分頁 -->
    <div class="pagination">
      <button class="btn btn-default" :disabled="page <= 1" @click="goPrevPage">&laquo; 上一頁</button>

      <button
        v-for="p in visiblePages"
        :key="p"
        class="btn"
        :class="p === page ? 'btn-primary' : 'btn-default'"
        @click="goToPage(p)"
      >
        {{ p }}
      </button>

      <span class="page-info">共 {{ total }} 筆</span>
      <button class="btn btn-default" :disabled="page >= totalPages" @click="goNextPage">下一頁 &raquo;</button>
    </div>

    <!-- 新增/編輯 - 共用彈跳視窗/Modal -->
    <div
      class="modal-backdrop"
      v-if="showModal"
    >
      <div class="modal-content">
        <button class="btn btn-default" @click="toggleBlock">
          {{ showBlock ? '隱藏' : '顯示' }}
        </button>
        <div v-show="showBlock">
          <h2 class="modal-title" v-if="isEdit">編輯海報 (ID: {{ formData.id }})</h2>
          <h2 class="modal-title" v-else>新增海報</h2>

          <!-- 標題 -->
          <label class="label">標題：</label>
          <input v-model="formData.title" placeholder="標題" class="input-text" />
          <!-- 請輸入SEO描述 ( 建議50~160字) -->
          <label class="label">請輸入SEO描述 ( 建議50~160字)：</label>
          <input v-model="formData.seo_description" class="input-text" />

          <!-- 「文字動畫 & 圖片動畫」在同一行 -->
          <div class="row-inline">
            <div class="row-block">
              <label class="label">文字動畫：</label>
              <select v-model="formData.textAnimation" class="nice-select">
                <option value="">文字正常顯示</option>
                <option value="fade-left">文字由左而右淡入</option>
                <option value="fade-right">文字由右而左淡入</option>
                <option value="fade-up">文字由下往上淡入</option>
                <option value="zoom-in">文字縮放放大淡入</option>
              </select>
            </div>
            <div class="row-block">
              <label class="label">圖片動畫：</label>
              <select v-model="formData.imageAnimation" class="nice-select">
                <option value="">圖片正常顯示</option>
                <option value="fade-left">圖片由左而右淡入</option>
                <option value="fade-right">圖片由右而左淡入</option>
                <option value="fade-up">圖片由下往上淡入</option>
                <option value="zoom-in">圖片縮放放大淡入</option>
              </select>
            </div>
          </div>

          <!-- 「版面配置 & 封面圖片 URL」在同一行 (手機版會自動一行一個) -->
          <div class="row-2col">
            <div class="col">
              <label class="label">版面配置：</label>
              <select v-model="formData.layoutType" class="nice-select">
                <option value="image-left">圖片左側</option>
                <option value="image-right">圖片右側</option>
                <option value="full-width">圖片全幅</option>
              </select>
            </div>
            <div class="col">
              <label class="label">封面圖片 URL：</label>
              <input
                v-model="formData.coverImage"
                placeholder="封面圖片URL"
                class="input-text"
              />
            </div>
          </div>

          <!-- 主圖預覽 -->
          <img
            v-if="formData.coverImage"
            :src="formData.coverImage"
            alt="封面預覽"
            class="cover-preview"
          />
        </div>

        <!-- TinyMCE 編輯器 -->
        <label class="label">內文：</label>
        <Editor
          ref="editorRef"
          v-model="formData.content"
          :init="editorInitOptions"
          @onInit="handleEditorInit"
        />
        <div class="modal-actions">
          <!-- 根據 isEdit 顯示不同按鈕文字 -->
          <button class="btn btn-primary" @click="createOrUpdatePoster">
            {{ isEdit ? '更新海報' : '新增海報' }}
          </button>
          <button class="btn btn-default" @click="closeModal">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref , computed, watch , nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import Editor from '@tinymce/tinymce-vue'
import 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/icons/default'
import 'tinymce/models/dom'
import 'tinymce/plugins/image'
import 'tinymce/plugins/media'
import 'tinymce/plugins/link'
import 'tinymce/plugins/code'
import { useAdminSysTempStore } from '@/store/adminSysTempStore.js'
import Swal from 'sweetalert2'

/*-------------------------------------
  1) 狀態與路由
--------------------------------------*/
const router = useRouter()
const posters = ref([])         // 撈回來的海報清單
const keyword = ref('')         // 搜尋關鍵字
const limit = ref(7)            // 每頁筆數
const offset = ref(0)           // 從哪一筆開始
const page = ref(1)             // 當前頁碼
const total = ref(0)            // 總筆數
const totalPages = ref(1)       // 總頁數

const editorRef = ref(null)
// 預設顯示
const showBlock = ref(true);

function toggleBlock() {
  showBlock.value = !showBlock.value;
}


/*-------------------------------------
  2) Modal 控制：showModal, isEdit
--------------------------------------*/
const showModal = ref(false)    // 是否顯示 Modal
const isEdit = ref(false)       // true = 編輯模式；false = 新增模式

/*-------------------------------------
  3) 新增/編輯共用表單資料
--------------------------------------*/
const formData = ref({
  id: null,
  title: '',
  content: '',
  coverImage: '',
  layoutType: 'image-left',
  imageWidth :  '50',
  textAnimation: '',
  imageAnimation: '',
  is_active: 1,
})

/*-------------------------------------
  4) TinyMCE 設定 (共用)
--------------------------------------*/
const editorInitOptions = {
  license_key: 'gpl',
  skin_url: '/tinymce/skins/ui/oxide',
  content_css: '/tinymce/skins/content/default/content.css',
  height: 800,
  menubar: true,
  plugins: 'image media link code',
  toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | code'
}

// 當 TinyMCE 初始化完成時觸發 (evt, editor)
function handleEditorInit(editor) {
  console.log('Tinymce 初始化完成, editor:', editor)
  editorRef.value = editor
  console.log('Tinymce 初始化完成, editor:', editor)
}

// 監聽 showBlock 的變化，當隱藏時把編輯器變大
watch(showBlock, async (newVal) => {
  // 等畫面更新後再取得 Editor 實例
  await nextTick()
  const editorInstance = editorRef.value?.editor

  console.log('editorInstance:', editorInstance)
  if (editorInstance) {
    if (!newVal) {
      // showBlock = false => 區塊隱藏時，讓編輯器變大 (例: 高度 500px)
      editorInstance.theme.resizeTo('100%', 500)
    } else {
      // showBlock = true => 還原高度 (例: 300px)
      editorInstance.theme.resizeTo('100%', 300)
    }
  }
})

/*-------------------------------------
  onMounted：檢查登入 & 取得列表
--------------------------------------*/
onMounted(async () => {
  const useAdminSysTemp = useAdminSysTempStore()
  await useAdminSysTemp.fetchAdminCheckLogin()
  if (!useAdminSysTemp.isLoggedIn) {
    router.push('/admin/login')
    return
  }
  // 取得海報列表
  await fetchPosters()
})

/*-------------------------------------
  5) 取得海報列表
--------------------------------------*/
async function fetchPosters() {
  try {
    const { data } = await axios.post('/api/admin/loadPosters', {
      limit: limit.value,
      offset: offset.value,
      keyword: keyword.value
    })
    if (data.success) {
      posters.value = data.rows
      total.value = data.total
      totalPages.value = Math.ceil(total.value / limit.value) || 1
    } else {
      console.error(data.message)
    }
  } catch (err) {
    console.error('取得海報失敗:', err)
  }
}

/*-------------------------------------
  6) 開啟 [新增海報] 模式
--------------------------------------*/
function openCreateModal() {
  isEdit.value = false
  // 清空 formData
  formData.value = {
    id: null,
    title: '',
    seo_description : undefined,
    content: '',
    coverImage: '',
    layoutType: 'image-left',
    imageWidth : '50',
    textAnimation: '',
    imageAnimation: '',
    is_active: 1,
  }
  showModal.value = true
}

/*-------------------------------------
  7) 開啟 [編輯海報] 模式
--------------------------------------*/
function openEditModal(item) {
  isEdit.value = true
  formData.value = {
    id: item.id,
    title: item.title,
    seo_description : item.seo_description,
    content: item.content,
    coverImage: item.coverImage,
    layoutType: item.layoutType || 'image-left',
    imageWidth : item.imageWidth || '50',
    textAnimation: item.textAnimation || '',
    imageAnimation: item.imageAnimation || '',
    is_active: item.is_active || 1,
  }
  showModal.value = true
}

/*-------------------------------------
  8) 新增 / 更新海報 (共用)
--------------------------------------*/
async function createOrUpdatePoster() {
  if (!isEdit.value) {
    // 新增海報
    await createPoster()
  } else {
    // 更新海報
    await updatePoster()
  }
}

/*-------------------------------------
  8-1) 新增海報
--------------------------------------*/
async function createPoster() {
  try {
    const payload = {
      title: formData.value.title,
      seo_description : formData.value.seo_description,
      content: formData.value.content,
      coverImage: formData.value.coverImage,
      layoutType: formData.value.layoutType,
      imageWidth : formData.value.imageWidth,
      textAnimation: formData.value.textAnimation,
      imageAnimation: formData.value.imageAnimation,
      is_active: formData.value.is_active,
    }
    if (!payload.title || !payload.content || !payload.coverImage) {
      Swal.fire({
        icon: 'error',
        title: '請填寫完整資料',
        text: '標題、內文、封面圖片為必填',
      })
      return
    }
    if(payload.seo_description == undefined){
      Swal.fire({
        icon: 'error',
        title: '請填寫完整資料',
        text: '請輸入SEO描述',
      })
      return
    }

    const { data } = await axios.post('/api/admin/createPoster', payload)
    if (data.success) {
      Swal.fire({
        icon: 'success',
        title: '新增成功',
        showConfirmButton: false,
        timer: 1500
      })
      closeModal()
      await fetchPosters()
    } else {
      Swal.fire({
        icon: 'error',
        title: '新增失敗',
        text: data.message || '新增失敗',
      })
    }
  } catch (error) {
    console.error(error)
    Swal.fire({
      icon: 'error',
      title: '新增失敗',
      text: '新增失敗',
    })
  }
}

/*-------------------------------------
  8-2) 更新海報
--------------------------------------*/
async function updatePoster() {
  try {
    const payload = {
      id: formData.value.id,
      title: formData.value.title,
      seo_description : formData.value.seo_description,
      content: formData.value.content,
      coverImage: formData.value.coverImage,
      imageWidth: formData.value.imageWidth,
      layoutType: formData.value.layoutType,
      textAnimation: formData.value.textAnimation,
      imageAnimation: formData.value.imageAnimation,
      is_active: formData.value.is_active,
    }
    if (!payload.title || !payload.content || !payload.coverImage) {
      Swal.fire({
        icon: 'error',
        title: '請填寫完整資料',
        text: '標題、內文、封面圖片為必填',
      })
      return
    }
    if(payload.seo_description == undefined){
      Swal.fire({
        icon: 'error',
        title: '請填寫完整資料',
        text: '請輸入SEO描述',
      })
      return
    }
    const { data } = await axios.post('/api/admin/updatePoster', payload)
    if (data.success) {
      Swal.fire({
        icon: 'success',
        title: '更新成功',
        showConfirmButton: false,
        timer: 1500
      })
      closeModal()
      await fetchPosters()
    } else {
      Swal.fire({
        icon: 'error',
        title: '更新失敗',
        text: data.message || '更新失敗',
      })
    }
  } catch (error) {
    console.error(error)
    Swal.fire({
      icon: 'error',
      title: '更新失敗',
      text: '更新失敗',
    })
  }
}

/*-------------------------------------
  8-3) 更新啟用狀態
--------------------------------------*/
async function updatePosterIsActive(item) {
  try {
    const payload = {
      id: item.id,
      is_active: item.is_active
    }
    const { data } = await axios.post('/api/admin/updatePosterIsActive', payload)
    if (data.success) {
      await fetchPosters()
    } else {
      Swal.fire({
        icon: 'error',
        title: '更新失敗',
        text: data.message || '更新失敗',
      })
    }
  } catch (error) {
    console.error(error)
    Swal.fire({
      icon: 'error',
      title: '更新失敗',
      text: '更新失敗',
    })
  }
}

/*-------------------------------------
  9) 關閉 Modal
--------------------------------------*/
function closeModal() {
  showModal.value = false
}

/*-------------------------------------
  10) 刪除海報
--------------------------------------*/
async function deletePoster(id) {

  Swal.fire({
    title: '確定要刪除此海報嗎？',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '確定',
    cancelButtonText: '取消',
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const { data } = await axios.post('/api/admin/deletePoster', { id })
        if (data.success) {
          await fetchPosters()
        } else {
         Swal.fire({
            icon: 'error',
            title: '刪除失敗',
            text: data.message || '刪除失敗',
          })
        }
      } catch (error) {
        console.error(error)
        Swal.fire({
          icon: 'error',
          title: '刪除失敗',
          text: '刪除失敗',
        })
      }
    }
  })
}

/*-------------------------------------
  11) 搜尋 & 分頁
--------------------------------------*/
async function onSearch() {
  page.value = 1
  offset.value = 0
  await fetchPosters()
}

const visiblePages = computed(() => {
  const t = totalPages.value
  const c = page.value
  const pages = []
  let start = Math.max(1, c - 2)
  let end = Math.min(t, c + 2)
  if (end - start < 4) {
    if (start === 1) end = Math.min(t, start + 4)
    else start = Math.max(1, end - 4)
  }
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

async function goToPage(p) {
  if (p < 1 || p > totalPages.value || p === page.value) return
  page.value = p
  offset.value = (p - 1) * limit.value
  await fetchPosters()
}

async function goPrevPage() {
  if (page.value <= 1) return
  page.value--
  offset.value = (page.value - 1) * limit.value
  await fetchPosters()
}

async function goNextPage() {
  if (page.value >= totalPages.value) return
  page.value++
  offset.value = (page.value - 1) * limit.value
  await fetchPosters()
}

</script>

<style scoped>
/* 容器、標題 */
.admin-posters {
  background-color: #fafafa;
  padding: 1rem;
  border-radius: 6px;
}

/* 搜尋列：電腦版橫排，手機版垂直 */
.search-bar {
  display: flex;
  flex-wrap: wrap; /* 手機版會自動換行 */
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.search-input {
  flex: 1;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn-default {
  background: #f3f3f3;
  color: #333;
}
.btn-primary {
  background-color: #007bff;
  color: #fff;
}
.btn-success {
  background-color: #28a745;
  color: #fff;
}
.btn-info {
  background-color: #17a2b8;
  color: #fff;
}
.btn-danger {
  background-color: #dc3545;
  color: #fff;
}

/* 資料表 */
.table-container {
  overflow-x: auto; /* 當表格太寬時可橫向捲動 */
}
.poster-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  min-width: 700px; /* 讓表格至少寬一點 (電腦) */
}

/* 表頭、表格單元格 */
.poster-table th,
.poster-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
}
.cover-image {
  width: 80px;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.no-data {
  color: #999;
}

/* 分頁 */
.pagination {
  margin: 1rem 0;
  display: flex;
  gap: 0.4rem;
  align-items: center;
  flex-wrap: wrap;
}
.page-info {
  margin: 0 8px;
  color: #888;
  font-size: 0.85rem;
}

/* Modal (共用) */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.modal-content {
  background: #fff;
  padding: 1rem 1.5rem;
  width: 900px; /* 電腦版視窗稍大 */
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.modal-title {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.2rem;
  color: #333;
}

/* label, input, select */
.label {
  display: block;
  margin: 1rem 0 0.2rem;
  color: #444;
  font-size: 0.95rem;
  font-weight: 500;
}
.input-text {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* 圖片預覽 */
.cover-preview {
  display: block;
  margin: 0.5rem 0;
  width: 200px;
  max-height: 150px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 6px;
}

/* 下拉框 */
.nice-select {
  width: 100%;
  padding: 6px 8px;
  margin-bottom: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  appearance: none;
  background: url("data:image/svg+xml;charset=US-ASCII,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,0 L10,0 L5,6 z' fill='%23666'/%3E%3C/svg%3E") no-repeat right 0.75rem center/8px 6px;
  background-color: #fff;
  background-position: right 1rem center;
}

/* Modal 底部按鈕 */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  gap: 0.5rem;
}

/* 將「文字動畫 & 圖片動畫」同列 */
.row-inline {
  display: flex;
  gap: 1rem;
}
.row-block {
  flex: 1;
}

/* 「版面配置 & 封面圖片 URL」同列（手機版自動一行一個） */
.row-2col {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.col {
  flex: 1 1 calc(50% - 1rem);
  /* 50% - gap，可自行調整 */
}

input {
  font-size: 1.0rem !important;
}
select {
  font-size: 1.0rem !important;
}

.modal-zoomed {
  width: 100vw !important;
  height: 100vh !important;
  max-height: 100vh !important;
  margin: 0;
  top: 0; 
  left: 0;
  border-radius: 0;
  /* 取消預設的 padding 或自行調整 */
  padding: 1rem;
}


.checkbox-label {
  display: inline-flex; /* 讓input + span排在同一列 */
  align-items: center;  /* 垂直置中 */
  cursor: pointer;      /* 滑鼠變成指標，可點擊 */
  gap: 8px;            /* checkbox 與文字之間的距離 */
}

.switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    margin-left: 8px;
    vertical-align: middle;
  }
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
  }
  .slider:before {
    position: absolute;
    content: "";
    height: 16px; width: 16px;
    left: 3px; bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  input:checked + .slider {
    background-color: #2196F3;
  }
  input:checked + .slider:before {
    transform: translateX(18px);
  }
/* 讓同個容器中的 btn 與 btn 之間有空隙 */
.btn + .btn {
  margin-left: 0.5rem;
}


/*---------------------------
  表格在手機版「往下伸展」
---------------------------*/
@media (max-width: 768px) {

  .poster-table {
    min-width: auto; /* 手機版移除min-width，避免超過螢幕 */
  }
  /* Modal縮小 */
  .modal-content {
    width: 90%;
    margin: 0 5%;
    max-height: 80vh;
  }

  /* 表格：隱藏表頭，改用 data-label 顯示 */
  .poster-table thead {
    display: none;
  }
  .poster-table,
  .poster-table tbody,
  .poster-table tr,
  .poster-table td {
    display: block;

  }
  .poster-table tr {
    margin-bottom: 1rem;
    border: 1px solid #ddd;
  }
  .poster-table td {
    border: none;
    border-bottom: 1px solid #eee;
    position: relative;
    padding-left: 40%;
    text-align: left; /* 文字左靠 */
  }
  .poster-table td:before {
    content: attr(data-label);
    position: absolute;
    left: 10px;
    font-weight: bold;
    color: #444;
  }
  .cover-image {
    margin-left: 0; /* 讓圖片在左側 */
  }
}
</style>
